import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Select, Empty } from 'antd';
import { isEmpty } from 'lodash';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import Papa from 'papaparse';

import EntityCardSkeleton from '../../components/EntityCardSkeleton';
import ErrorPage from '../../../../../shared/ErrorPage';
import Spinner from '../../../../../shared/designSystem/components/Spinner';
import DualAxisAreaChart from '../../../../../shared/designSystem/components/DualAxisAreaChart';
import DateRangeMenu from '../../../../../shared/designSystem/components/DateRangeMenu';

import DetailBar from './components/DetailBar';
import PageViewsTable from './components/PageViewsTable';

// =============================================================================
// PageViewTabView — V1 facelift to Design System v2.
//
// Layout: `.mjl-ds campaign-page-view-v1` wrapper. Performance section
// (mono eyebrow + DateRangePicker in head, KpiBar + axis-picker chart in
// body) on top, Page Views table section below. Date filtering keeps the
// same `dataFilterChange(start, end)` controller hook the BizCharts internal
// date-picker used to call — only the picker chrome moves to the section
// header.
// =============================================================================

const AXIS_OPTIONS = [
  { key: 'pageViews', label: 'Page Views' },
  { key: 'impressions', label: 'Impressions' },
  { key: 'uniqueUsers', label: 'Unique Users' },
];

const PageViewChart = ({ byDate }) => {
  const [leftY, setLeftY] = useState('Page Views');
  const [rightY, setRightY] = useState('Impressions');

  const rows = useMemo(() => {
    if (isEmpty(byDate)) return [];
    return byDate.map((row) => ({
      xAxis: row.date,
      pageViews: row.pageViews,
      impressions: row.impressions,
      uniqueUsers: row.uniqueUsers,
    }));
  }, [byDate]);

  if (isEmpty(rows)) {
    return (
      <div
        className="campaign-overview__chart"
        style={{ padding: '32px 20px', display: 'flex', justifyContent: 'center' }}
      >
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No chart data yet" />
      </div>
    );
  }

  return (
    <div className="campaign-overview__chart">
      <div className="campaign-overview__chart-head">
        <div className="campaign-overview__chart-eyebrow mono-eyebrow">Trend</div>

        <div className="campaign-overview__axis-row">
          <div className="campaign-overview__axis-field">
            <span className="campaign-overview__axis-label mono-eyebrow">
              <span className="campaign-overview__axis-dot campaign-overview__axis-dot--left" />
              Left axis
            </span>
            <Select
              value={leftY}
              onSelect={setLeftY}
              className="campaign-overview__axis-select"
              getPopupContainer={(trigger) => trigger.parentNode}
              popupClassName="mjl-ds"
            >
              {AXIS_OPTIONS.filter((op) => op.label !== rightY).map((op) => (
                <Select.Option key={op.key} value={op.label}>
                  {op.label}
                </Select.Option>
              ))}
            </Select>
          </div>

          <div className="campaign-overview__axis-field campaign-overview__axis-field--right">
            <span className="campaign-overview__axis-label mono-eyebrow">
              <span className="campaign-overview__axis-dot campaign-overview__axis-dot--right" />
              Right axis
            </span>
            <Select
              value={rightY}
              onSelect={setRightY}
              className="campaign-overview__axis-select"
              getPopupContainer={(trigger) => trigger.parentNode}
              popupClassName="mjl-ds"
            >
              {AXIS_OPTIONS.filter((op) => op.label !== leftY).map((op) => (
                <Select.Option key={op.key} value={op.label}>
                  {op.label}
                </Select.Option>
              ))}
            </Select>
          </div>
        </div>
      </div>

      <div className="campaign-overview__chart-body">
        <DualAxisAreaChart dualChartData={rows} leftY={leftY} rightY={rightY} />
      </div>
    </div>
  );
};

const PageViewTabView = ({
  campaignOrder,
  reportData,
  reportError,
  reportLoading,
  signedUrlError,
  signedUrlLoading,
  setExportButtonLoading,
  setExportButtonDisable,
  setExportButtonMenuItems,
  dataFilterChange = () => {},
}) => {
  document.title = `${campaignOrder.name} - Page View`;
  const { startDate, endDate, name } = campaignOrder;
  const [byDate, setByDate] = useState();
  const [summary, setSummary] = useState();
  const [pageViews, setPageViews] = useState();
  const [dateRange, setDateRange] = useState(null);
  const [rangeType, setRangeType] = useState('Custom');

  useEffect(() => {
    if (reportLoading || signedUrlLoading) setExportButtonLoading(true);
    else setExportButtonLoading(false);
  }, [reportLoading, signedUrlLoading, setExportButtonLoading]);

  useEffect(() => {
    if (reportData) {
      setByDate(reportData.byDate);
      setSummary(reportData.summary);
      setPageViews(reportData.attributedPageViews);
      setExportButtonDisable(false);
    }
  }, [reportData, setExportButtonDisable]);

  useEffect(() => {
    if (byDate) {
      setExportButtonMenuItems([
        {
          key: 'pageViewByDate',
          text: 'Page View By Date',
          onClick: () => {
            setExportButtonLoading(true);
            const key = 'Page View By Date';
            const zip = new JSZip();
            const folder = zip.folder(`${name.replace(/\//g, ' ')}-${key}}`);
            const formattedData = byDate.map((item) => ({
              Date: moment(item.date, 'YYYY-MM-DD').utc(true).format('YYYY-MM-DD'),
              'Page Views': item.pageViews,
              Impressions: item.impressions,
              'Unique Users': item.uniqueUsers,
            }));
            folder.file(`${name} - Page View By Date.csv`, Papa.unparse(formattedData));
            zip.generateAsync({ type: 'blob' }).then(
              (blob) => {
                saveAs(blob, `${name.replace(/\//g, ' ')}-Page View By Date`);
                return true;
              },
              function (e) {
                console.log('error', e);
                return false;
              }
            );
            setExportButtonLoading(false);
          },
        },
        {
          key: 'pageViewImpressions',
          text: 'Page View Impressions',
          onClick: () => {
            setExportButtonLoading(true);
            const key = 'Page View Impressions';
            const zip = new JSZip();
            const folder = zip.folder(`${name.replace(/\//g, ' ')}-${key}}`);
            const formattedData = pageViews.map((item) => ({
              'Page URL': item.pageUrl,
              'Page View Time': item.pageViewTime,
              Impressions: item.totalImpressions,
              'First Impressions Time': item.firstImpressionTime,
            }));
            folder.file(
              `${name} - Page View Impressions.csv`,
              Papa.unparse(formattedData)
            );
            zip.generateAsync({ type: 'blob' }).then(
              (blob) => {
                saveAs(blob, `${name.replace(/\//g, ' ')}-Page View Impressions`);
                return true;
              },
              function (e) {
                console.log('error', e);
                return false;
              }
            );
            setExportButtonLoading(false);
          },
        },
      ]);
    }
  }, [byDate, name, pageViews, setExportButtonLoading, setExportButtonMenuItems]);

  // Flight-date anchor for the header DateRangePicker — clamps `disabledDate`
  // and provides the "Custom" pill fallback label.
  const defaultDateRange = useMemo(() => {
    if (!startDate || !endDate) return null;
    return [moment.utc(startDate), moment.utc(endDate)];
  }, [startDate, endDate]);

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    // 'All Time' preset resolves to an empty range → reset to the unfiltered
    // original report rather than calling dataFilterChange (which returns
    // undefined for an absent range and would leave the previous filter
    // stuck on screen).
    const hasRange = dates && dates.length === 2 && dates[0] && dates[1];
    if (!hasRange) {
      if (reportData) {
        setByDate(reportData.byDate);
        setSummary(reportData.summary);
        setPageViews(reportData.attributedPageViews);
      }
      return;
    }
    const report = dataFilterChange(dates[0], dates[1]);
    if (!report) return;
    setByDate(report.byDate);
    setSummary(report.summary);
    setPageViews(report.attributedPageViews);
  };

  if (reportError || signedUrlError) {
    return <ErrorPage errorMessage="Unable to load Report" />;
  }

  // Guard against the render window between `reportLoading` flipping false
  // and the useEffect populating `summary` / `byDate` / `pageViews`.
  if (reportLoading || signedUrlLoading || !summary) {
    return <EntityCardSkeleton />;
  }

  return (
    <div className="mjl-ds campaign-page-view-v1">
      <Spinner spinning={reportLoading || signedUrlLoading}>
        <section className="campaign-overview__performance">
          <header className="campaign-overview__performance-head">
            <div className="campaign-overview__performance-eyebrow mono-eyebrow">
              Performance
            </div>
            <DateRangeMenu
              rangeType={rangeType}
              onRangeTypeChange={setRangeType}
              value={dateRange}
              onChange={handleDateRangeChange}
              defaultDateRange={defaultDateRange}
            />
          </header>

          <DetailBar
            pageViews={summary.pageViews}
            impressions={summary.impressions}
            uniqueUsers={summary.uniqueUsers}
            startDate={startDate}
            endDate={endDate}
          />

          <PageViewChart byDate={byDate} />
        </section>
      </Spinner>

      <PageViewsTable data={pageViews} />
    </div>
  );
};

export default PageViewTabView;
